import { memo } from "react";
import { Handle, Position, NodeProps } from "reactflow";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import type { OrgNode } from "@/types";
import { distinctDepartment } from "@/lib/org-chart-department";

interface OrgNodeData extends OrgNode {
  isCollapsed: boolean;
  depth: number;
  onToggleCollapse?: (nodePid: string) => void;
  onNodeClick?: (node: OrgNode) => void;
  isHighlighted?: boolean;
}

/**
 * Custom React Flow node component for org chart
 */
function OrgChartFlowNodeComponent({ data }: NodeProps<OrgNodeData>) {
  const hasReports = data.reportCount > 0;
  
  // Generate initials for avatar fallback
  const initials = data.fullName
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);
  const department = distinctDepartment(data.position, data.department);

  const handleNodeClick = () => {
    if (data.onNodeClick) {
      // Create OrgNode without React Flow specific props
      const { isCollapsed, depth, onToggleCollapse, onNodeClick, isHighlighted, ...orgNode } = data;
      data.onNodeClick(orgNode as OrgNode);
    }
  };

  const handleToggleCollapse = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (data.onToggleCollapse) {
      data.onToggleCollapse(data.pid);
    }
  };

  return (
    <>
      {/* Connection handles */}
      <Handle type="target" position={Position.Top} className="!bg-border !w-2 !h-2" />
      
      <Card
        className={cn(
          "cursor-pointer transition-all hover:shadow-lg hover:border-primary/20",
          "min-w-[200px] max-w-[250px] border-2",
          data.isHighlighted && "ring-2 ring-primary shadow-lg border-primary"
        )}
        onClick={handleNodeClick}
      >
        {/* Collapse/Expand Button */}
        {hasReports && data.onToggleCollapse && (
          <Button
            variant="ghost"
            size="icon"
            className="absolute -bottom-4 left-1/2 -translate-x-1/2 h-8 w-8 rounded-full bg-background border-2 border-border hover:border-primary/50 shadow-md hover:shadow-lg transition-all z-10"
            onClick={handleToggleCollapse}
          >
            {data.isCollapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <ChevronDown className="h-4 w-4" />
            )}
          </Button>
        )}

        {/* Collapsed Count Badge */}
        {data.isCollapsed && hasReports && (
          <Badge 
            variant="secondary" 
            className="absolute -bottom-4 right-4 z-10"
          >
            +{data.reportCount}
          </Badge>
        )}

        <div className="flex items-start gap-3 p-4">
          {/* Avatar */}
          <Avatar className="h-12 w-12 flex-shrink-0">
            <AvatarImage src={data.avatarUrl} alt={data.fullName} />
            <AvatarFallback className="bg-primary text-primary-foreground">
              {initials}
            </AvatarFallback>
          </Avatar>

          {/* Member Info */}
          <div className="flex-1 min-w-0">
            <h3 className="font-semibold text-sm truncate" title={data.fullName}>
              {data.fullName}
            </h3>
            
            {data.position && (
              <p className="text-xs text-muted-foreground truncate" title={data.position}>
                {data.position}
              </p>
            )}
            
            {department && (
              <Badge variant="outline" className="mt-1 text-xs">
                {department}
              </Badge>
            )}
          </div>
        </div>
      </Card>

      <Handle type="source" position={Position.Bottom} className="!bg-border !w-2 !h-2" />
    </>
  );
}

export const OrgChartFlowNode = memo(OrgChartFlowNodeComponent);
